import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NovaApiService, SavedFile } from '../../../services/api-services/nova-api.service';
import { NotificationService } from '../../../services/notification.service';
import { UtcDateTimePipe } from '../../../pipes/utc-date-time.pipe';
import { NovaServiceService } from '../../../services/nova-service.service';
import { Subscription } from 'rxjs';
import html2canvas from 'html2canvas';
import {
  fetchNovaFile,
  isS3PresignedUrlErrorBody,
  NOVA_FILE_UNAVAILABLE_MESSAGE
} from '../../../shared/nova-presigned-url.util';

interface SavedFileGroup {
  title: string;
  files: SavedFile[];
}

@Component({
  selector: 'app-nova-storage',
  standalone: true,
  imports: [CommonModule, UtcDateTimePipe, MatTooltipModule],
  templateUrl: './nova-storage.component.html',
  styleUrl: './nova-storage.component.scss'
})
export class NovaStorageComponent implements OnInit, OnDestroy {
  // Chart thumbnail dimensions
  private static readonly CHART_THUMBNAIL_SIZE = 123;

  savedFiles: SavedFile[] = [];
  fileGroups: SavedFileGroup[] = [];
  isLoading: boolean = false;
  error: string | null = null;

  // Confirmation popup state
  showDeleteConfirmation: boolean = false;
  fileToDelete: SavedFile | null = null;
  isDeleting: boolean = false;

  // Image viewer popup state
  showImageViewer: boolean = false;
  viewerImageUrl: string = '';
  /** When viewer loads chart via fetch+blob, revoke on close */
  private viewerBlobUrl: string | null = null;
  /** Saved files whose presigned URL returned S3 error (or failed thumbnail load) */
  private expiredFileIds = new Set<string>();
  private presignedToastOnceIds = new Set<string>();

  readonly fileUnavailableMessage = NOVA_FILE_UNAVAILABLE_MESSAGE;

  // History state
  isHistoryOpen: boolean = true;

  // Cache for file icons and safe URLs to prevent reloading
  private fileIconCache = new Map<string, string>();
  private safeUrlCache = new Map<string, SafeResourceUrl>();
  // Cache for blob URLs to prevent flickering
  private blobUrlCache = new Map<string, string>();
  // Track click timeouts to prevent single-click action on double-click
  private clickTimeouts = new Map<string, NodeJS.Timeout>();
  // Subscription for history state
  private historySubscription?: Subscription;
  // UtcDateTimePipe for parsing dates
  private utcDateTimePipe = new UtcDateTimePipe();

  constructor(
    private novaApiService: NovaApiService,
    private sanitizer: DomSanitizer,
    private notificationService: NotificationService,
    private novaService: NovaServiceService
  ) {}

  ngOnInit() {
    this.loadSavedFiles();
    // Subscribe to history state changes
    this.historySubscription = this.novaService.isHistoryOpen$.subscribe(
      isOpen => {
        this.isHistoryOpen = isOpen;
      }
    );
  }

  ngOnDestroy() {
    if (this.viewerBlobUrl) {
      URL.revokeObjectURL(this.viewerBlobUrl);
      this.viewerBlobUrl = null;
    }
    // Unsubscribe to prevent memory leaks
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
  }

  loadSavedFiles() {
    this.isLoading = true;
    this.error = null;

    this.novaApiService.getSavedFiles({
      period: 'all',
      page: 1,
      page_size: 1000
    }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.savedFiles = response.data.files || [];
          this.precomputeCaches();
          this.groupFiles();
        } else {
          this.error = response.message || 'Failed to load saved files';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading saved files:', error);
        this.error = error?.error?.message || error?.message || 'Failed to load saved files. Please try again.';
        this.isLoading = false;
      }
    });
  }

  private precomputeCaches() {
    // Pre-compute and cache icon URLs and safe URLs for all files
    this.savedFiles.forEach(file => {
      // Cache file icon
      const iconUrl = this.getFileIconInternal(file);
      this.fileIconCache.set(file.saved_file_id, iconUrl);

      // For chart files, pre-fetch and cache blob URL to prevent flickering
      if (file.file_type === 'chart' && file.file_url && !this.blobUrlCache.has(file.saved_file_id)) {
        // Pre-fetch HTML content and create blob URL
        fetch(file.file_url)
          .then(async response => {
            const htmlContent = await response.text();
            if (!response.ok || isS3PresignedUrlErrorBody(htmlContent)) {
              this.expiredFileIds.add(file.saved_file_id);
              return;
            }
            const modifiedHtml = this.removePlotlyToolbar(htmlContent);
            const blob = new Blob([modifiedHtml], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);

            // Cache the blob URL
            this.blobUrlCache.set(file.saved_file_id, blobUrl);

            // Only update safe URL cache if it doesn't already have a blob-based URL
            // This prevents overwriting the cache after it's been used
            if (!this.safeUrlCache.has(file.saved_file_id)) {
              const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
              this.safeUrlCache.set(file.saved_file_id, safeUrl);
            }
          })
          .catch(error => {
            // console.warn('Failed to pre-fetch chart HTML:', error);
            // Fallback to original URL only if not already cached
            if (!this.safeUrlCache.has(file.saved_file_id) && file.file_url) {
              const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.file_url);
              this.safeUrlCache.set(file.saved_file_id, safeUrl);
            }
          });
      } else {
        // For non-chart files, cache safe URL normally (only if not already cached)
        if (file.file_url && !this.safeUrlCache.has(file.saved_file_id)) {
          const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.file_url);
          this.safeUrlCache.set(file.saved_file_id, safeUrl);
        }
      }
    });
  }

  private groupFiles() {
    if (!this.savedFiles || this.savedFiles.length === 0) {
      this.fileGroups = [];
      return;
    }

    // Group files based on saved_timestamp
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    const todayFiles: SavedFile[] = [];
    const yesterdayFiles: SavedFile[] = [];
    const last7DaysFiles: SavedFile[] = [];
    const thisMonthFiles: SavedFile[] = [];
    const lastMonthFiles: SavedFile[] = [];
    const olderFiles: SavedFile[] = [];

    // Sort files by saved_timestamp (newest first)
    const sortedFiles = [...this.savedFiles].sort((a, b) => {
      const dateA = a.saved_timestamp ? new Date(a.saved_timestamp).getTime() : 0;
      const dateB = b.saved_timestamp ? new Date(b.saved_timestamp).getTime() : 0;
      return dateB - dateA;
    });

    sortedFiles.forEach(file => {
      const fileDate = file.saved_timestamp ? new Date(file.saved_timestamp) : new Date(0);
      const fileDateOnly = new Date(fileDate.getFullYear(), fileDate.getMonth(), fileDate.getDate());

      if (fileDateOnly.getTime() === today.getTime()) {
        todayFiles.push(file);
      } else if (fileDateOnly.getTime() === yesterday.getTime()) {
        yesterdayFiles.push(file);
      } else if (fileDate >= last7Days) {
        last7DaysFiles.push(file);
      } else if (fileDate >= thisMonth) {
        thisMonthFiles.push(file);
      } else if (fileDate >= lastMonth && fileDate <= lastMonthEnd) {
        lastMonthFiles.push(file);
      } else {
        olderFiles.push(file);
      }
    });

    const groups: SavedFileGroup[] = [];

    if (todayFiles.length > 0) {
      groups.push({ title: 'Today', files: todayFiles });
    }
    if (yesterdayFiles.length > 0) {
      groups.push({ title: 'Yesterday', files: yesterdayFiles });
    }
    if (last7DaysFiles.length > 0) {
      groups.push({ title: 'Last 7 Days', files: last7DaysFiles });
    }
    if (thisMonthFiles.length > 0) {
      groups.push({ title: 'This Month', files: thisMonthFiles });
    }
    if (lastMonthFiles.length > 0) {
      groups.push({ title: 'Last Month', files: lastMonthFiles });
    }
    if (olderFiles.length > 0) {
      groups.push({ title: 'Older', files: olderFiles });
    }

    this.fileGroups = groups;
  }

  isChartFile(file: SavedFile): boolean {
    return file.file_type === 'chart';
  }

  getFileIcon(file: SavedFile): string {
    // Return cached icon URL to prevent reloading
    if (this.fileIconCache.has(file.saved_file_id)) {
      return this.fileIconCache.get(file.saved_file_id)!;
    }
    // Fallback to computing it (shouldn't happen after precomputeCaches)
    const iconUrl = this.getFileIconInternal(file);
    this.fileIconCache.set(file.saved_file_id, iconUrl);
    return iconUrl;
  }

  private getFileIconInternal(file: SavedFile): string {
    if (file.file_type === 'chart') {
      return 'assets/icons/navigator/chart-icon.svg';
    }

    // Extract file extension from file_url to determine appropriate icon
    if (!file.file_url) return 'assets/icons/navigator/file-icon.svg';

    const urlParts = file.file_url.split('/');
    const fileName = urlParts[urlParts.length - 1].split('?')[0];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));

    const iconMap: { [key: string]: string } = {
      '.csv': 'assets/icons/navigator/csv-file-icon.svg',
      '.pdf': 'assets/icons/navigator/csv-file-icon.svg',
      '.doc': 'assets/icons/navigator/csv-file-icon.svg',
      '.docx': 'assets/icons/navigator/csv-file-icon.svg',
      '.xls': 'assets/icons/navigator/csv-file-icon.svg',
      '.xlsx': 'assets/icons/navigator/csv-file-icon.svg',
      '.ppt': 'assets/icons/navigator/csv-file-icon.svg',
      '.pptx': 'assets/icons/navigator/csv-file-icon.svg',
      '.txt': 'assets/icons/navigator/csv-file-icon.svg',
      '.zip': 'assets/icons/navigator/csv-file-icon.svg',
      '.rar': 'assets/icons/navigator/csv-file-icon.svg',
      '.7z': 'assets/icons/navigator/csv-file-icon.svg',
      '.gz': 'assets/icons/navigator/csv-file-icon.svg',
    };

    return iconMap[extension] || 'assets/icons/navigator/csv-file-icon.svg';
  }

  getFileTitle(file: SavedFile): string {
    // Extract filename from URL or use a default title
    if (file.file_url) {
      const urlParts = file.file_url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      if (fileName && fileName !== '') {
        // Remove .html.gz or .gz extension
        const nameWithoutExt = fileName.replace(/\.(html\.gz|gz|html)$/i, '');
        // Convert saved_file_xxx to a readable title
        if (nameWithoutExt.startsWith('saved_file_')) {
          return this.formatFileName(nameWithoutExt);
        }
        return nameWithoutExt;
      }
    }
    return `Saved ${file.file_type === 'chart' ? 'Chart' : 'File'}`;
  }

  private formatFileName(fileName: string): string {
    // Try to extract meaningful title from saved_file_id
    // For now, return a generic title based on file type
    if (fileName.includes('chart') || fileName.includes('Chart')) {
      return 'Saved Chart';
    }
    return 'Saved File';
  }

  formatDateFromPipe(dateString: string | undefined): string {
    if (!dateString) return '';

    // Use utc-date-time pipe to parse and validate the date (handles timezone conversion)
    // The pipe validates the date and handles timezone, we use the same date parsing
    const pipeOutput = this.utcDateTimePipe.transform(dateString);
    if (!pipeOutput) return '';

    // Parse the date using the same logic as the pipe (handles timezone)
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      // Format as DD/MM/YYYY (matching the screenshot format)
      // The pipe already handled timezone conversion, we just format the date part
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      // Get timezone abbreviation (same logic as UtcDateTimePipe)
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timeZoneAbbr = this.getTimeZoneAbbr(timeZone);

      return `${day}/${month}/${year} ${timeZoneAbbr}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  private getTimeZoneAbbr(timeZone: string): string {
    // Map of IANA timezone names to their abbreviations (same as UtcDateTimePipe)
    const timeZoneMap: { [key: string]: string } = {
      'Asia/Kolkata': 'IST',
      'Asia/Calcutta': 'IST',
      'America/Los_Angeles': 'PST',
      'America/New_York': 'EST',
      'Europe/London': 'GMT',
      'Asia/Dubai': 'GST',
      'Asia/Singapore': 'SGT',
      'Australia/Sydney': 'AEST',
      'Pacific/Auckland': 'NZST',
      'Europe/Paris': 'CET',
      'Asia/Tokyo': 'JST'
    };

    // If we have a mapping, use it
    if (timeZoneMap[timeZone]) {
      return timeZoneMap[timeZone];
    }

    // If no mapping exists, try to get abbreviation from the date
    const date = new Date();
    const options = { timeZone, timeZoneName: 'short' as const };
    const timeZoneAbbr = new Intl.DateTimeFormat('en-US', options)
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value;

    return timeZoneAbbr || timeZone;
  }

  isTitleTruncated(file: SavedFile): boolean {
    const title = file.saved_file_title || this.getFileTitle(file);
    // Check if title is likely to be truncated (more than 50 characters as a rough estimate)
    return !!(title && title.length > 50);
  }

  isFileUrlExpired(file: SavedFile): boolean {
    return this.expiredFileIds.has(file.saved_file_id);
  }

  onSavedFileThumbnailError(file: SavedFile): void {
    this.expiredFileIds.add(file.saved_file_id);
    if (this.presignedToastOnceIds.has(file.saved_file_id)) {
      return;
    }
    this.presignedToastOnceIds.add(file.saved_file_id);
    this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
  }

  getSafeUrl(file: SavedFile): SafeResourceUrl {
    // Always check safe URL cache first to prevent creating new objects
    if (this.safeUrlCache.has(file.saved_file_id)) {
      return this.safeUrlCache.get(file.saved_file_id)!;
    }

    // For chart files, prioritize blob URL if available to prevent flickering
    if (file.file_type === 'chart') {
      // Check if blob URL is cached (pre-fetched)
      if (this.blobUrlCache.has(file.saved_file_id)) {
        const blobUrl = this.blobUrlCache.get(file.saved_file_id)!;
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
        // Cache and return
        this.safeUrlCache.set(file.saved_file_id, safeUrl);
        return safeUrl;
      }

      // If not cached, create temporary safe URL (will be updated when blob is ready)
      if (file.file_url) {
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.file_url);
        this.safeUrlCache.set(file.saved_file_id, safeUrl);
        return safeUrl;
      }
    } else {
      // For non-chart files, create and cache safe URL
      if (file.file_url) {
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(file.file_url);
        this.safeUrlCache.set(file.saved_file_id, safeUrl);
        return safeUrl;
      }
    }

    // Return empty safe URL as fallback
    const emptySafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
    this.safeUrlCache.set(file.saved_file_id, emptySafeUrl);
    return emptySafeUrl;
  }

  // TrackBy functions to prevent unnecessary re-renders
  trackByGroupTitle(index: number, group: SavedFileGroup): string {
    return group.title;
  }

  trackByFileId(index: number, file: SavedFile): string {
    return file.saved_file_id;
  }

  // File click handler - open file on single click
  onFileClick(file: SavedFile, event: Event) {
    // Don't handle click if clicking on bookmark icon or download button
    const target = event.target as HTMLElement;
    if (target.closest('.bookmark-icon') || target.closest('.download-button')) {
      return;
    }

    // Stop event propagation immediately to prevent parent handlers
    event.stopPropagation();
    event.preventDefault();

    // Clear any existing timeout for this file
    const existingTimeout = this.clickTimeouts.get(file.saved_file_id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.clickTimeouts.delete(file.saved_file_id);
    }

    if (!file.file_url) {
      return;
    }

    // Delay action slightly to allow double-click detection
    // But execute immediately if no double-click occurs
    const timeout = setTimeout(() => {
      this.clickTimeouts.delete(file.saved_file_id);

      // For chart files, open in popup viewer on single click
      if (file.file_type === 'chart') {
        this.openImageViewer(file);
      }
      // For other files, download on single click
      else {
        this.downloadFile(file);
      }
    }, 250); // 250ms delay to detect double-click

    this.clickTimeouts.set(file.saved_file_id, timeout);
  }

  // File double-click handler - same as single click for consistency
  onFileDoubleClick(file: SavedFile, event: Event) {
    // Don't handle double-click if clicking on bookmark icon or download button
    const target = event.target as HTMLElement;
    if (target.closest('.bookmark-icon') || target.closest('.download-button')) {
      return;
    }

    // Stop event propagation immediately
    event.stopPropagation();
    event.preventDefault();

    // Cancel any pending single-click action
    const existingTimeout = this.clickTimeouts.get(file.saved_file_id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.clickTimeouts.delete(file.saved_file_id);
    }

    if (!file.file_url) {
      return;
    }

    // For chart files, open in popup viewer immediately
    if (file.file_type === 'chart') {
      this.openImageViewer(file);
    }
    // For other files, download immediately
    else {
      this.downloadFile(file);
    }
  }

  // Download button click handler
  onDownloadClick(file: SavedFile, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    if (!file.file_url) {
      this.notificationService.error('Error', 'File URL not available');
      return;
    }

    // For chart files, download as JPEG image
    if (file.file_type === 'chart') {
      this.downloadChartAsJPEG(file, event);
    } else {
      // For other files, download them as-is
      this.downloadFile(file);
    }
  }

  /** True if file URL or title indicates a PDF (so we can set blob type and .pdf extension). */
  private isPdfFile(file: SavedFile): boolean {
    if (file.file_url) {
      const path = file.file_url.split('?')[0].toLowerCase();
      if (path.endsWith('.pdf')) return true;
    }
    const title = (file.saved_file_title || this.getFileTitle(file) || '').toLowerCase();
    return title.endsWith('.pdf');
  }

  /** True if file URL or title indicates CSV (same idea as PDF for downloads). */
  private isCsvFile(file: SavedFile): boolean {
    if (file.file_url) {
      const path = file.file_url.split('?')[0].toLowerCase();
      if (path.endsWith('.csv')) return true;
    }
    const title = (file.saved_file_title || this.getFileTitle(file) || '').toLowerCase();
    return title.endsWith('.csv');
  }

  // Download file
  private downloadFile(file: SavedFile) {
    if (!file.file_url) return;

    // Fetch as blob with credentials so binary (e.g. PDF) is preserved and same-origin auth works
    fetchNovaFile(file.file_url)
      .then(async response => {
        if (!response.ok) {
          const text = await response.text();
          if (isS3PresignedUrlErrorBody(text)) {
            this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
        const blob = await response.blob();
        return { blob, contentType };
      })
      .then(result => {
        if (!result) {
          return;
        }
        const { blob, contentType } = result;
        // Use correct blob type for PDF/CSV so the save dialog and OS show the right file kind
        const isPdf = this.isPdfFile(file);
        const isCsv =
          this.isCsvFile(file) ||
          contentType.includes('text/csv') ||
          contentType.includes('application/csv') ||
          contentType.includes('text/comma-separated-values');
        const csvBlobTypeOk = blob.type.toLowerCase().startsWith('text/csv');
        const downloadBlob =
          isPdf && blob.type !== 'application/pdf'
            ? new Blob([blob], { type: 'application/pdf' })
            : isCsv && !csvBlobTypeOk
              ? new Blob([blob], { type: 'text/csv' })
              : blob;
        const url = window.URL.createObjectURL(downloadBlob);
        const link = document.createElement('a');
        link.href = url;

        // Extract filename from URL or use a default; ensure PDF/CSV have correct extensions
        let filename = this.getFileTitle(file);
        const urlParts = file.file_url!.split('/');
        const urlFileName = urlParts[urlParts.length - 1].split('?')[0];
        if (urlFileName && urlFileName !== '') {
          filename = urlFileName;
        }
        const titled = (file.saved_file_title || '').trim();
        if (titled) {
          filename = titled;
        }
        if (isPdf && filename && !filename.toLowerCase().endsWith('.pdf')) {
          const baseName = filename.includes('.') ? filename.substring(0, filename.lastIndexOf('.')) : filename;
          filename = `${baseName}.pdf`;
        }
        if (isCsv && (!filename || !filename.toLowerCase().endsWith('.csv'))) {
          const baseName =
            filename && filename.includes('.')
              ? filename.substring(0, filename.lastIndexOf('.'))
              : filename || 'download';
          filename = `${baseName}.csv`;
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the temporary URL
        window.URL.revokeObjectURL(url);

        // Show success notification
        this.notificationService.success('Success', 'File Downloaded Successfully');
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        this.notificationService.error('Error', 'Could not download the file. Please try again or reload the page.');
      });
  }

  // Delete file methods
  onBookmarkClick(file: SavedFile, event: Event) {
    event.stopPropagation();
    this.fileToDelete = file;
    this.showDeleteConfirmation = true;
  }

  confirmDelete() {
    if (!this.fileToDelete || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.novaApiService.deleteSavedFile({
      saved_file_id: this.fileToDelete.saved_file_id
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.notificationService.success(
            'Success',
            response.message || 'File removed successfully'
          );
          // Remove file from saved files array
          this.savedFiles = this.savedFiles.filter(
            file => file.saved_file_id !== this.fileToDelete!.saved_file_id
          );
          // Clear caches for deleted file
          this.fileIconCache.delete(this.fileToDelete!.saved_file_id);
          this.safeUrlCache.delete(this.fileToDelete!.saved_file_id);

          // Revoke and clear blob URL cache
          const blobUrl = this.blobUrlCache.get(this.fileToDelete!.saved_file_id);
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }
          this.blobUrlCache.delete(this.fileToDelete!.saved_file_id);

          // Regroup files
          this.precomputeCaches();
          this.groupFiles();
        } else {
          this.notificationService.error(
            'Error',
            response.message || 'Failed to remove file'
          );
        }
        this.isDeleting = false;
        this.cancelDelete();
      },
      error: (error) => {
        console.error('Error removing file:', error);
        const errorMessage = error?.error?.message || error?.message || 'Failed to remove file. Please try again.';
        this.notificationService.error('Error', errorMessage);
        this.isDeleting = false;
        this.cancelDelete();
      }
    });
  }

  cancelDelete() {
    this.showDeleteConfirmation = false;
    this.fileToDelete = null;
    this.isDeleting = false;
  }

  // Remove Plotly toolbar from HTML content
  private removePlotlyToolbar(htmlContent: string): string {
    // Inject CSS to hide toolbar immediately and fix chart layout
    const toolbarRemovalCSS = `
      <style>
        .modebar,
        .modebar-container,
        .js-plotly-plot .modebar,
        .plotly .modebar,
        .plotly-notifier,
        .modebar-btn {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          pointer-events: none !important;
        }
        .plotly {
          overflow: visible !important;
          width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
          height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
        }
        .js-plotly-plot {
          overflow: visible !important;
          width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
          height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
        }
        body {
          overflow: visible !important;
          padding: 0 !important;
          margin: 0 !important;
          width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
          height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
        }
        html {
          width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
          height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important;
          overflow: hidden !important;
        }
      </style>
    `;

    // Inject script to disable Plotly toolbar
    const toolbarRemovalScript = `
      <script>
        (function() {
          // CSS to hide toolbar and fix chart layout
          const style = document.createElement('style');
          const chartSize = ${NovaStorageComponent.CHART_THUMBNAIL_SIZE};
          style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; } .plotly { overflow: visible !important; width: ' + chartSize + 'px !important; height: ' + chartSize + 'px !important; } .js-plotly-plot { overflow: visible !important; width: ' + chartSize + 'px !important; height: ' + chartSize + 'px !important; } body { overflow: visible !important; padding: 0 !important; margin: 0 !important; width: ' + chartSize + 'px !important; height: ' + chartSize + 'px !important; } html { width: ' + chartSize + 'px !important; height: ' + chartSize + 'px !important; overflow: hidden !important; }';
          document.head.appendChild(style);

          // Set body and html dimensions
          document.body.style.width = chartSize + 'px';
          document.body.style.height = chartSize + 'px';
          document.body.style.padding = '0';
          document.body.style.margin = '0';
          document.documentElement.style.width = chartSize + 'px';
          document.documentElement.style.height = chartSize + 'px';
          document.documentElement.style.overflow = 'hidden';

          function removePlotlyToolbar() {
            // Remove toolbar elements directly via DOM
            const toolbars = document.querySelectorAll('.modebar, .modebar-container, .plotly .modebar');
            toolbars.forEach(function(toolbar) {
              if (toolbar && toolbar.parentNode) {
                toolbar.parentNode.removeChild(toolbar);
              }
              toolbar.style.display = 'none';
              toolbar.style.visibility = 'hidden';
              toolbar.style.opacity = '0';
              toolbar.style.height = '0';
              toolbar.style.width = '0';
            });

            // Remove toolbar from existing plots
            if (window.Plotly) {
              const plots = document.querySelectorAll('.plotly');
              plots.forEach(function(plot) {
                if (plot && plot._fullLayout) {
                  plot._fullLayout.modebar = { display: 'never' };
                  // Fix layout margins for axis labels and set size
                  plot.style.width = chartSize + 'px';
                  plot.style.height = chartSize + 'px';
                  if (!plot._fullLayout.margin || plot._fullLayout.margin.b < 40) {
                    plot._fullLayout.margin = {
                      l: 40,
                      r: 20,
                      t: 20,
                      b: 40
                    };
                    if (window.Plotly && window.Plotly.relayout) {
                      window.Plotly.relayout(plot, {
                        margin: plot._fullLayout.margin,
                        width: chartSize,
                        height: chartSize
                      });
                    }
                  }
                  if (plot._fullLayout._modeBar) {
                    plot._fullLayout._modeBar.style.display = 'none';
                    plot._fullLayout._modeBar.style.visibility = 'hidden';
                    if (plot._fullLayout._modeBar.parentNode) {
                      plot._fullLayout._modeBar.parentNode.removeChild(plot._fullLayout._modeBar);
                    }
                  }
                  // Force update
                  if (plot._fullLayout._modeBarDiv) {
                    plot._fullLayout._modeBarDiv.style.display = 'none';
                    if (plot._fullLayout._modeBarDiv.parentNode) {
                      plot._fullLayout._modeBarDiv.parentNode.removeChild(plot._fullLayout._modeBarDiv);
                    }
                  }
                }
              });

              // Override Plotly.newPlot to disable toolbar for new plots and fix layout
              if (!window.Plotly._originalNewPlot) {
                window.Plotly._originalNewPlot = window.Plotly.newPlot;
                window.Plotly.newPlot = function(...args) {
                  let config = args[args.length - 1];
                  if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                    config.displayModeBar = false;
                  } else {
                    args.push({ displayModeBar: false });
                  }
                  const result = window.Plotly._originalNewPlot.apply(this, args);
                  // Remove toolbar and fix layout after plot is created
                  setTimeout(function() {
                    const plots = document.querySelectorAll('.plotly');
                    plots.forEach(function(plot) {
                      const toolbars = plot.querySelectorAll('.modebar, .modebar-container');
                      toolbars.forEach(function(tb) {
                        tb.style.display = 'none';
                        if (tb.parentNode) tb.parentNode.removeChild(tb);
                      });
                      // Fix layout margins for axis labels and set size
                      if (plot._fullLayout) {
                        plot._fullLayout.margin = {
                          l: 40,
                          r: 20,
                          t: 20,
                          b: 40
                        };
                        plot.style.width = chartSize + 'px';
                        plot.style.height = chartSize + 'px';
                        window.Plotly.relayout(plot, {
                          margin: plot._fullLayout.margin,
                          width: chartSize,
                          height: chartSize
                        });
                      }
                    });
                  }, 100);
                  return result;
                };
              }

              // Override Plotly.react to disable toolbar
              if (!window.Plotly._originalReact) {
                window.Plotly._originalReact = window.Plotly.react;
                window.Plotly.react = function(...args) {
                  let config = args[args.length - 1];
                  if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                    config.displayModeBar = false;
                  } else {
                    args.push({ displayModeBar: false });
                  }
                  return window.Plotly._originalReact.apply(this, args);
                };
              }
            }
          }

          // Remove immediately
          removePlotlyToolbar();

          // Try to remove immediately
          if (document.readyState === 'complete' || document.readyState === 'interactive') {
            removePlotlyToolbar();
          } else {
            document.addEventListener('DOMContentLoaded', removePlotlyToolbar);
          }

          // Also try after delays in case Plotly loads asynchronously
          setTimeout(removePlotlyToolbar, 50);
          setTimeout(removePlotlyToolbar, 100);
          setTimeout(removePlotlyToolbar, 200);
          setTimeout(removePlotlyToolbar, 500);
          setTimeout(removePlotlyToolbar, 1000);
          setTimeout(removePlotlyToolbar, 2000);
          setTimeout(removePlotlyToolbar, 3000);

          // Watch for new plots
          if (window.MutationObserver) {
            const observer = new MutationObserver(function() {
              removePlotlyToolbar();
            });
            if (document.body) {
              observer.observe(document.body, { childList: true, subtree: true });
            }
          }
        })();
      </script>
    `;

    // Insert CSS in head first
    if (htmlContent.includes('</head>')) {
      htmlContent = htmlContent.replace('</head>', toolbarRemovalCSS + '</head>');
    } else if (htmlContent.includes('<head>')) {
      htmlContent = htmlContent.replace('<head>', '<head>' + toolbarRemovalCSS);
    } else {
      htmlContent = '<head>' + toolbarRemovalCSS + '</head>' + htmlContent;
    }

    // Insert script before closing body tag, or before closing head tag if no body
    if (htmlContent.includes('</body>')) {
      htmlContent = htmlContent.replace('</body>', toolbarRemovalScript + '</body>');
    } else if (htmlContent.includes('</head>')) {
      htmlContent = htmlContent.replace('</head>', toolbarRemovalScript + '</head>');
    } else {
      // If no body or head, append at the end
      htmlContent = htmlContent + toolbarRemovalScript;
    }

    return htmlContent;
  }

  // Download chart as JPEG image
  private async downloadChartAsJPEG(file: SavedFile, event: Event): Promise<void> {
    if (!file.file_url) {
      this.notificationService.error('Error', 'File URL not available');
      return;
    }

    try {
      // Chart size for download (large size) - matches nova.component.ts
      const downloadWidth = 800;
      const downloadHeight = 600;

      // Try to fetch HTML content first to avoid CORS issues
      let htmlContent: string;
      try {
        const response = await fetch(file.file_url);
        const raw = await response.text();
        if (!response.ok || isS3PresignedUrlErrorBody(raw)) {
          if (isS3PresignedUrlErrorBody(raw) || !response.ok) {
            this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
          }
          return;
        }
        htmlContent = this.removePlotlyToolbar(raw);
      } catch (fetchError) {
        // console.warn('Could not fetch HTML content, will try iframe approach:', fetchError);
        htmlContent = '';
      }

      // Create a temporary iframe to load the HTML
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = `${downloadWidth}px`;
      iframe.style.height = `${downloadHeight}px`;
      iframe.style.border = 'none';

      // If we have HTML content, use blob URL to avoid CORS
      if (htmlContent) {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        iframe.src = blobUrl;
      } else {
        // Fallback to direct URL (may have CORS issues)
        iframe.src = file.file_url;
      }

      document.body.appendChild(iframe);

      // Wait for iframe to load
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (htmlContent) {
            URL.revokeObjectURL(iframe.src);
          }
          document.body.removeChild(iframe);
          reject(new Error('Timeout loading iframe'));
        }, 15000); // 15 second timeout

        iframe.onload = () => {
          clearTimeout(timeout);
          // Additional delay to ensure content is fully rendered
          setTimeout(() => {
            resolve();
          }, 2000);
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          if (htmlContent) {
            URL.revokeObjectURL(iframe.src);
          }
          document.body.removeChild(iframe);
          reject(new Error('Failed to load iframe'));
        };
      });

      // Get iframe content - check if we can access it (may fail due to CORS)
      let iframeDoc: Document | null = null;
      try {
        iframeDoc = iframe.contentDocument || iframe.contentWindow?.document || null;
      } catch (e) {
        // CORS error - cannot access iframe content
        // console.warn('Cannot access iframe content due to CORS.');
        if (htmlContent) {
          URL.revokeObjectURL(iframe.src);
        }
        document.body.removeChild(iframe);
        this.notificationService.error('Error', 'Cannot generate image due to CORS restrictions. Please download the file directly.');
        return;
      }

      if (!iframeDoc || !iframeDoc.body) {
        if (htmlContent) {
          URL.revokeObjectURL(iframe.src);
        }
        document.body.removeChild(iframe);
        this.notificationService.error('Error', 'Could not access chart content');
        return;
      }

      // Remove Plotly toolbar from iframe document before capturing
      try {
        const toolbars = iframeDoc.querySelectorAll('.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn');
        toolbars.forEach((toolbar) => {
          (toolbar as HTMLElement).style.display = 'none';
          (toolbar as HTMLElement).style.visibility = 'hidden';
          (toolbar as HTMLElement).style.opacity = '0';
          (toolbar as HTMLElement).style.height = '0';
          (toolbar as HTMLElement).style.width = '0';
          (toolbar as HTMLElement).style.pointerEvents = 'none';
          try {
            if (toolbar.parentNode) {
              toolbar.parentNode.removeChild(toolbar);
            }
          } catch (e) {
            // Ignore errors
          }
        });

        // Inject CSS to hide toolbar in iframe document
        const existingStyle = iframeDoc.querySelector('style[data-plotly-toolbar-removal]');
        if (!existingStyle) {
          const style = iframeDoc.createElement('style');
          style.setAttribute('data-plotly-toolbar-removal', 'true');
          style.textContent = `
            .modebar,
            .modebar-container,
            .js-plotly-plot .modebar,
            .plotly .modebar,
            .plotly-notifier,
            .modebar-btn {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              pointer-events: none !important;
            }
          `;
          iframeDoc.head.appendChild(style);
        }
      } catch (e) {
        // Ignore errors - toolbar removal will be handled in onclone
      }

      // Use html2canvas to capture the iframe content
      const canvas = await html2canvas(iframeDoc.body, {
        width: downloadWidth,
        height: downloadHeight,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: downloadWidth,
        windowHeight: downloadHeight,
        onclone: (clonedDoc) => {
          // Ensure cloned body is visible
          const clonedBody = clonedDoc.body;
          if (clonedBody) {
            clonedBody.style.display = 'block';
            clonedBody.style.visibility = 'visible';
          }

          // Remove Plotly toolbar from cloned document before capturing
          const toolbars = clonedDoc.querySelectorAll('.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn');
          toolbars.forEach((toolbar) => {
            (toolbar as HTMLElement).style.display = 'none';
            (toolbar as HTMLElement).style.visibility = 'hidden';
            (toolbar as HTMLElement).style.opacity = '0';
            (toolbar as HTMLElement).style.height = '0';
            (toolbar as HTMLElement).style.width = '0';
            (toolbar as HTMLElement).style.pointerEvents = 'none';
            try {
              if (toolbar.parentNode) {
                toolbar.parentNode.removeChild(toolbar);
              }
            } catch (e) {
              // Ignore errors
            }
          });

          // Inject CSS to hide toolbar in cloned document
          const style = clonedDoc.createElement('style');
          style.textContent = `
            .modebar,
            .modebar-container,
            .js-plotly-plot .modebar,
            .plotly .modebar,
            .plotly-notifier,
            .modebar-btn {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              pointer-events: none !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        }
      });

      // Convert canvas to JPEG data URL
      const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.9);

      // Download the JPEG
      this.downloadJPEGFromDataUrl(jpegDataUrl, file);

      // Clean up
      if (htmlContent) {
        URL.revokeObjectURL(iframe.src);
      }
      document.body.removeChild(iframe);

      // Show success notification
      this.notificationService.success('Success', 'File Downloaded Successfully');
    } catch (error) {
      console.error('Error generating JPEG from chart:', error);
      this.notificationService.error('Error', 'Failed to generate large image for download');
    }
  }

  // Download JPEG from data URL - matches nova.component.ts downloadJPEGImage method
  private downloadJPEGFromDataUrl(jpegDataUrl: string, file: SavedFile): void {
    // Convert data URL to blob and download
    fetch(jpegDataUrl)
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Generate filename with .jpeg extension - matches nova.component.ts logic
        // Extract filename from URL (same as attachment.name in nova.component.ts)
        let fileName = '';
        if (file.file_url) {
          const urlParts = file.file_url.split('/');
          fileName = urlParts[urlParts.length - 1].split('?')[0];
        }

        // Remove HTML extensions and add .jpeg - matches attachment.name.replace(/\.(html|htm|gz)$/i, '')
        const baseName = fileName.replace(/\.(html|htm|gz)$/i, '');
        link.download = `${baseName}.jpeg`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Error downloading JPEG:', error);
        this.notificationService.error('Error', 'Failed to download chart image');
      });
  }

  // Image viewer methods
  openImageViewer(file: SavedFile): void {
    void this.openSavedFileInViewer(file);
  }

  private async openSavedFileInViewer(file: SavedFile): Promise<void> {
    if (!file.file_url) {
      return;
    }

    if (this.viewerBlobUrl) {
      URL.revokeObjectURL(this.viewerBlobUrl);
      this.viewerBlobUrl = null;
    }

    try {
      const response = await fetchNovaFile(file.file_url);
      if (!response.ok) {
        const errText = await response.text();
        if (isS3PresignedUrlErrorBody(errText)) {
          this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
        } else {
          this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
        }
        return;
      }

      if (file.file_type === 'chart') {
        const htmlText = await response.text();
        if (isS3PresignedUrlErrorBody(htmlText)) {
          this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
          return;
        }
        const blob = new Blob([htmlText], { type: 'text/html' });
        this.viewerBlobUrl = URL.createObjectURL(blob);
      } else {
        const blob = await response.blob();
        this.viewerBlobUrl = URL.createObjectURL(blob);
      }

      this.viewerImageUrl = this.viewerBlobUrl;
      this.showImageViewer = true;
    } catch (e) {
      console.error('Error opening saved file viewer:', e);
      this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
    }
  }

  closeImageViewer(): void {
    if (this.viewerBlobUrl) {
      URL.revokeObjectURL(this.viewerBlobUrl);
      this.viewerBlobUrl = null;
    }
    this.showImageViewer = false;
    this.viewerImageUrl = '';
  }

  getSafeUrlForViewer(url: string): SafeResourceUrl {
    // Check if we already have a cached SafeResourceUrl for this URL
    if (this.safeUrlCache.has(url)) {
      return this.safeUrlCache.get(url)!;
    }

    // Create and cache a new SafeResourceUrl
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.safeUrlCache.set(url, safeUrl);
    return safeUrl;
  }

  removePlotlyToolbarFromViewer(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        // Check if style already injected
        const existingStyle = iframeDoc.querySelector('style[data-plotly-toolbar-removal-viewer]');
        if (!existingStyle) {
          // Inject CSS first
          const style = iframeDoc.createElement('style');
          style.setAttribute('data-plotly-toolbar-removal-viewer', 'true');
          style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; }';
          iframeDoc.head.appendChild(style);
        }
      }
    } catch (e) {
      // CORS - can't access iframe content
    }
  }

  // Remove Plotly toolbar from iframe (for chart display)
  removePlotlyToolbarFromIframe(event: Event, file: SavedFile): void {
    const iframe = event.target as HTMLIFrameElement;

    // NEVER change iframe.src in this method to prevent reloading
    // The iframe src is already set via [src] binding from getSafeUrl()
    // This method only handles toolbar removal, not URL changes

    // Try to inject toolbar removal script if needed (only if CORS allows)
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        // Check if style already injected
        const existingStyle = iframeDoc.querySelector('style[data-plotly-toolbar-removal]');
        if (!existingStyle) {
          // Inject CSS first
          const style = iframeDoc.createElement('style');
          style.setAttribute('data-plotly-toolbar-removal', 'true');
          style.textContent = `.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; } .plotly { overflow: visible !important; width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; } .js-plotly-plot { overflow: visible !important; width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; } body { overflow: visible !important; padding: 0 !important; margin: 0 !important; width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; } html { width: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; height: ${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px !important; overflow: hidden !important; }`;
          iframeDoc.head.appendChild(style);

          // Set body and html dimensions
          iframeDoc.body.style.width = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
          iframeDoc.body.style.height = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
          iframeDoc.body.style.padding = '0';
          iframeDoc.body.style.margin = '0';
          iframeDoc.documentElement.style.width = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
          iframeDoc.documentElement.style.height = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
          iframeDoc.documentElement.style.overflow = 'hidden';

          // Set plot dimensions
          const plots = iframeDoc.querySelectorAll('.plotly');
          plots.forEach((plot) => {
            (plot as HTMLElement).style.width = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
            (plot as HTMLElement).style.height = `${NovaStorageComponent.CHART_THUMBNAIL_SIZE}px`;
          });
        }
      }
    } catch (e) {
      // CORS - can't access iframe content, toolbar removal is handled in HTML
    }
  }
}
